import { Code } from 'lucide-react'
import type { StockTemplate } from '@/pages/template-stock'
import { STOCK_TEMPLATES } from '@/pages/template-stock'

interface TemplatePickerProps {
  onSelect: (template: StockTemplate) => void
  onBlank: () => void
}

export function TemplatePicker({ onSelect, onBlank }: TemplatePickerProps): React.JSX.Element {
  return (
    <div className="mx-auto max-w-3xl space-y-8 py-10">
      <div className="text-center">
        <h1 className="text-2xl font-semibold text-text-primary">Choose a starting point</h1>
        <p className="mt-1 text-sm text-text-secondary">Pick a template and make it yours.</p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {STOCK_TEMPLATES.filter((t) => t.id !== 'blank').map((template) => (
          <button
            key={template.id}
            type="button"
            onClick={() => onSelect(template)}
            className="group cursor-pointer rounded-xl border border-surface-3 bg-surface-0 p-0 text-left transition-colors hover:border-brand-300 hover:bg-surface-1"
          >
            <div className="flex h-36 items-center justify-center rounded-t-xl bg-surface-2 p-4">
              <Thumbnail kind={template.id} />
            </div>
            <div className="px-4 py-3">
              <h3 className="font-medium text-text-primary">{template.name}</h3>
              <p className="mt-0.5 truncate text-sm text-text-secondary">{template.description}</p>
            </div>
          </button>
        ))}
      </div>

      <button
        type="button"
        onClick={onBlank}
        className="flex w-full items-center justify-between rounded-xl border border-surface-3 bg-surface-0 px-5 py-4 text-left transition-colors hover:border-brand-300 hover:bg-surface-1"
      >
        <div className="flex items-center gap-3">
          <Code className="h-5 w-5 text-text-tertiary" />
          <div>
            <span className="font-medium text-text-primary">Blank</span>
            <span className="ml-1.5 text-sm text-text-secondary">
              — start from scratch with just the responsive boilerplate
            </span>
          </div>
        </div>
        <span className="shrink-0 text-sm font-medium text-brand-500">Start &rarr;</span>
      </button>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/*  Thumbnail illustrations — simple div arrangements per template    */
/* ------------------------------------------------------------------ */

function Thumbnail({ kind }: { kind: string }): React.JSX.Element {
  const base = 'w-full max-w-[140px]'

  switch (kind) {
    case 'newsletter':
      return (
        <div className={`${base} space-y-1.5`}>
          <div className="h-10 rounded bg-brand-500/20" />
          <div className="flex gap-1.5">
            <div className="h-8 flex-1 rounded bg-brand-500/15" />
            <div className="h-8 flex-1 rounded bg-brand-500/15" />
          </div>
          <div className="h-3 rounded-sm bg-brand-500/10" />
        </div>
      )
    case 'announcement':
      return (
        <div className={`${base} space-y-1.5`}>
          <div className="h-12 rounded bg-brand-500/20" />
          <div className="h-2 w-4/5 rounded-sm bg-brand-500/15" />
          <div className="h-2 w-3/5 rounded-sm bg-brand-500/10" />
          <div className="mx-auto mt-1 h-5 w-20 rounded bg-brand-500/25" />
        </div>
      )
    case 'promotion':
      return (
        <div className={`${base} space-y-1.5`}>
          <div className="h-4 w-3/4 rounded-sm bg-brand-500/25" />
          <div className="h-10 rounded bg-brand-500/15" />
          <div className="mx-auto h-6 w-24 rounded bg-brand-500/30" />
        </div>
      )
    case 'digest':
      return (
        <div className={`${base} space-y-1.5`}>
          {[0.2, 0.18, 0.15, 0.12].map((opacity, i) => (
            <div key={i} className="flex items-center gap-1.5">
              <div className="h-2 w-2 shrink-0 rounded-full" style={{ background: `rgba(var(--brand-500-rgb, 99,102,241), ${opacity + 0.1})` }} />
              <div className="h-2 flex-1 rounded-sm" style={{ background: `rgba(var(--brand-500-rgb, 99,102,241), ${opacity})` }} />
            </div>
          ))}
        </div>
      )
    case 'feedback':
      return (
        <div className={`${base} space-y-2`}>
          <div className="flex justify-center gap-1">
            {[1, 2, 3, 4, 5].map((n) => (
              <StarShape key={n} filled={n <= 3} />
            ))}
          </div>
          <div className="h-2 w-full rounded-sm bg-brand-500/10" />
          <div className="h-2 w-3/4 rounded-sm bg-brand-500/10" />
          <div className="mx-auto h-5 w-16 rounded bg-brand-500/20" />
        </div>
      )
    case 'event':
      return (
        <div className={`${base} flex items-center gap-3`}>
          <div className="flex h-12 w-12 shrink-0 flex-col items-center justify-center rounded-lg bg-brand-500/25">
            <span className="text-[8px] font-bold uppercase leading-none text-brand-500/70">Jun</span>
            <span className="text-base font-bold leading-tight text-brand-500/80">14</span>
          </div>
          <div className="space-y-1">
            <div className="h-2 w-16 rounded-sm bg-brand-500/20" />
            <div className="h-2 w-12 rounded-sm bg-brand-500/12" />
          </div>
        </div>
      )
    default:
      return (
        <div className={`${base} space-y-1.5`}>
          <div className="h-6 rounded bg-brand-500/15" />
          <div className="h-2 w-3/4 rounded-sm bg-brand-500/10" />
          <div className="h-2 w-1/2 rounded-sm bg-brand-500/10" />
        </div>
      )
  }
}

function StarShape({ filled }: { filled: boolean }): React.JSX.Element {
  return (
    <div
      className={`h-4 w-4 ${filled ? 'text-brand-500/60' : 'text-brand-500/20'}`}
      style={{
        clipPath: 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)',
        background: 'currentColor',
      }}
    />
  )
}
